import React, { FC, useState, useContext, useEffect, useRef } from 'react';
import {
	Box,
	Button,
	CircularProgress,
	FormControl,
	InputLabel,
	MenuItem,
	Select,
	Stack,
	Switch,
	Typography,
} from '@mui/material';
import { makeStyles } from 'tss-react/mui';
import axios from 'axios';
import AdminStringList from '@/app2/shared/components/AdminStringList';
import { AdminInputText } from '@/pages/ManagerView/ContentCreation/AdminInputText';
import { AdminFieldContainer } from '@/pages/ManagerView/ContentCreation/AdminFieldContainer';
import {
	useCreateQuizQuestion,
	type CreateQuizQuestionBody,
	type CreateQuizQuestionResponse,
	type QuestionSlim,
} from '@/app2/shared/data';
import {
	App2NotificationContext,
	App2NotificationSeverity,
} from '@/app2/shared/contexts/App2NotificationContext';
import { palette } from '@/constants';

const useStyles = makeStyles()((theme) => ({
	root: {
		position: 'relative',
	},
	mainRow: {
		display: 'flex',
		flexDirection: 'row',
		alignItems: 'flex-start',
		gap: theme.spacing(3),
		[theme.breakpoints.down('sm')]: {
			flexDirection: 'column',
		},
	},
	formColumn: {
		flex: 1,
		minWidth: 0,
		width: '100%',
	},
	typeSelectWrap: {
		marginBottom: theme.spacing(2),
		minWidth: 200,
		maxWidth: 240,
		width: 'auto',
	},
	typeSelectInput: {
		'& .MuiOutlinedInput-notchedOutline': {
			borderColor: `${palette.grey15} !important`,
		},
		'&:hover .MuiOutlinedInput-notchedOutline': {
			borderColor: `${palette.grey40} !important`,
		},
		'&.Mui-focused .MuiOutlinedInput-notchedOutline': {
			borderWidth: 1,
			borderColor: `${palette.orange} !important`,
		},
		'& .MuiSelect-select': {
			display: 'flex',
			alignItems: 'center',
			minHeight: 36,
			paddingTop: theme.spacing(1),
			paddingBottom: theme.spacing(1),
		},
	},
	formCard: {
		border: `1px solid ${theme.palette.divider}`,
		borderRadius: theme.shape.borderRadius,
		padding: theme.spacing(3),
		backgroundColor: theme.palette.background.paper,
	},
	sidebarColumn: {
		width: 300,
		flexShrink: 0,
		alignSelf: 'flex-start',
		position: 'sticky',
		top: theme.spacing(2),
		display: 'flex',
		flexDirection: 'column',
		gap: theme.spacing(2),
		[theme.breakpoints.down('sm')]: {
			width: '100%',
			position: 'relative',
			top: 'auto',
		},
	},
	saveAboveInfo: {
		display: 'flex',
		justifyContent: 'flex-end',
		width: '100%',
	},
	infoTile: {
		border: `1px solid ${theme.palette.divider}`,
		borderRadius: '8px',
		padding: theme.spacing(2.5, 2.5, 2.25),
		backgroundColor: theme.palette.background.paper,
	},
	infoSectionTitle: {
		fontSize: '0.6875rem',
		fontWeight: 700,
		letterSpacing: '0.12em',
		textTransform: 'uppercase',
		color: theme.palette.text.primary,
		display: 'block',
		marginBottom: theme.spacing(2),
		paddingBottom: theme.spacing(1.5),
		borderBottom: `1px solid ${theme.palette.divider}`,
	},
	infoMetaBlock: {
		display: 'flex',
		flexDirection: 'column',
		gap: theme.spacing(1.75),
		marginBottom: theme.spacing(2.5),
		'&:last-child': {
			marginBottom: 0,
		},
	},
	infoKvRow: {
		display: 'flex',
		flexDirection: 'row',
		alignItems: 'baseline',
		justifyContent: 'space-between',
		gap: theme.spacing(2),
	},
	infoKvLabel: {
		fontSize: '0.8125rem',
		fontWeight: 700,
		lineHeight: 1.4,
		color: theme.palette.text.primary,
		textAlign: 'left',
		flex: '1 1 auto',
		minWidth: 0,
	},
	infoKvValue: {
		fontSize: '0.8125rem',
		fontWeight: 500,
		lineHeight: 1.4,
		color: theme.palette.text.primary,
		textAlign: 'right',
		flex: '0 0 auto',
		whiteSpace: 'nowrap',
	},
	createTitle: {
		fontSize: '34px',
		fontWeight: 700,
		lineHeight: 1.24,
		color: theme.palette.text.primary,
	},
	saveBtn: {
		textTransform: 'uppercase',
		fontWeight: 600,
		fontSize: '0.875rem',
		borderRadius: '6px',
		borderColor: `${palette.orange} !important`,
		color: `${palette.white} !important`,
		'&:hover': {
			borderColor: `${palette.orange} !important`,
			backgroundColor: palette.orange08,
		},
		'&.Mui-disabled': {
			borderColor: 'rgba(255, 158, 0, 0.35) !important',
			color: 'rgba(255, 255, 255, 0.35) !important',
		},
	},
}));

export type QuestionKind =
	| 'boolean_choice_questions'
	| 'single_choice_questions'
	| 'multiple_choice_questions';

const TYPE_OPTIONS: { value: QuestionKind; label: string }[] = [
	{ value: 'boolean_choice_questions', label: 'True/False' },
	{ value: 'single_choice_questions', label: 'Single choice' },
	{ value: 'multiple_choice_questions', label: 'Multiple choice' },
];

const HEADER_TITLE: Record<QuestionKind, string> = {
	boolean_choice_questions: 'Create questionBoolean',
	single_choice_questions: 'Create questionSingleChoice',
	multiple_choice_questions: 'Create questionMultipleChoice',
};

const SUCCESS_TOAST_TYPE: Record<CreateQuizQuestionBody['questionType'], string> = {
	boolean_choice_questions: 'questionBoolean',
	single_choice_questions: 'questionSingleChoice',
	multiple_choice_questions: 'questionMultipleChoice',
};

export interface AddQuizNewQuestionFormProps {
	onSaved: (slim: QuestionSlim) => void;
	onDraftEdited?: () => void;
}

export const AddQuizNewQuestionForm: FC<AddQuizNewQuestionFormProps> = ({ onSaved, onDraftEdited }) => {
	const { classes } = useStyles();
	const { notify } = useContext(App2NotificationContext);
	const createQuestion = useCreateQuizQuestion();

	const [questionKind, setQuestionKind] = useState<QuestionKind>('single_choice_questions');
	const [title, setTitle] = useState('');
	const [hint, setHint] = useState('');
	const [boolAnswer, setBoolAnswer] = useState(false);
	const [options, setOptions] = useState<string[]>([]);
	const [correctAnswer, setCorrectAnswer] = useState('');
	const [correctAnswers, setCorrectAnswers] = useState<string[]>([]);

	const skipDraftEffect = useRef(true);
	useEffect(() => {
		if (skipDraftEffect.current) {
			skipDraftEffect.current = false;
			return;
		}
		onDraftEdited?.();
	}, [title, hint, boolAnswer, options, correctAnswer, correctAnswers, questionKind, onDraftEdited]);

	const buildBody = (): CreateQuizQuestionBody | null => {
		const t = title.trim();
		if (!t) {
			notify('Title is required', 'Validation', App2NotificationSeverity.warning, 5000);
			return null;
		}
		if (questionKind === 'boolean_choice_questions') {
			return {
				questionType: 'boolean_choice_questions',
				title: t,
				hint: hint.trim() || undefined,
				answer: boolAnswer,
			};
		}
		if (questionKind === 'single_choice_questions') {
			const opts = options.map((s) => s.trim()).filter(Boolean);
			const ca = correctAnswer.trim();
			if (opts.length === 0) {
				notify('At least one option is required', 'Validation', App2NotificationSeverity.warning, 5000);
				return null;
			}
			if (!ca || !opts.includes(ca)) {
				notify(
					'Correct answer must match one of the options',
					'Validation',
					App2NotificationSeverity.warning,
					5000
				);
				return null;
			}
			return {
				questionType: 'single_choice_questions',
				title: t,
				hint: hint.trim() || undefined,
				options: opts,
				correctAnswer: ca,
			};
		}
		const opts = options.map((s) => s.trim()).filter(Boolean);
		const cans = correctAnswers.map((s) => s.trim()).filter(Boolean);
		if (opts.length === 0) {
			notify('At least one option is required', 'Validation', App2NotificationSeverity.warning, 5000);
			return null;
		}
		if (cans.length === 0) {
			notify('At least one correct answer is required', 'Validation', App2NotificationSeverity.warning, 5000);
			return null;
		}
		const setOpts = new Set(opts);
		for (const a of cans) {
			if (!setOpts.has(a)) {
				notify(`Correct answer must be one of the options: ${a}`, 'Validation', App2NotificationSeverity.warning, 5000);
				return null;
			}
		}
		return {
			questionType: 'multiple_choice_questions',
			title: t,
			hint: hint.trim() || undefined,
			options: opts,
			correctAnswers: cans,
		};
	};

	const handleSave = async (): Promise<void> => {
		const body = buildBody();
		if (!body) return;
		try {
			const data: CreateQuizQuestionResponse = await createQuestion.mutateAsync(body);
			notify(
				`Created new ${SUCCESS_TOAST_TYPE[body.questionType]}`,
				'Success',
				App2NotificationSeverity.success,
				3000
			);
			onSaved({
				id: data.id,
				__type: data.__type,
				title: data.title ?? '',
				hint: data.hint ?? null,
				answer: data.answer ?? null,
				options: data.options ?? null,
				correctAnswer: data.correctAnswer ?? null,
				correctAnswers: data.correctAnswers ?? null,
			});
		} catch (e: unknown) {
			let msg = 'Failed to save question';
			if (axios.isAxiosError(e)) {
				const d = e.response?.data as { message?: string | string[] } | undefined;
				if (typeof d?.message === 'string') msg = d.message;
				else if (Array.isArray(d?.message)) msg = d.message.join(', ');
			}
			notify(msg, 'Error', App2NotificationSeverity.error, 5000);
		}
	};

	const pending = createQuestion.isPending;

	return (
		<Box className={classes.root}>
			{pending && (
				<Box
					sx={{
						position: 'absolute',
						inset: 0,
						display: 'flex',
						alignItems: 'center',
						justifyContent: 'center',
						bgcolor: 'action.hover',
						zIndex: 2,
						borderRadius: 1,
					}}
				>
					<CircularProgress />
				</Box>
			)}
			<Box className={classes.mainRow}>
				<Box className={classes.formColumn}>
					<FormControl className={classes.typeSelectWrap} variant="outlined" size="small">
						<InputLabel id="add-quiz-new-qtype-label" shrink>
							Question Type
						</InputLabel>
						<Select<QuestionKind>
							className={classes.typeSelectInput}
							labelId="add-quiz-new-qtype-label"
							label="Question Type"
							value={questionKind}
							onChange={(e) => setQuestionKind(e.target.value as QuestionKind)}
							notched
						>
							{TYPE_OPTIONS.map((o) => (
								<MenuItem key={o.value} value={o.value}>
									{o.label}
								</MenuItem>
							))}
						</Select>
					</FormControl>

					<Box className={classes.formCard}>
						<Stack spacing={3}>
							<Typography className={classes.createTitle} component="h2" variant="inherit">
								{HEADER_TITLE[questionKind]}
							</Typography>

							{questionKind === 'boolean_choice_questions' && (
								<Stack spacing={2}>
									<AdminInputText
										required
										labelPlacement="above"
										label="Title"
										value={title}
										onChange={(e) => setTitle(e.target.value)}
									/>
									<AdminInputText
										labelPlacement="above"
										label="hint"
										value={hint}
										onChange={(e) => setHint(e.target.value)}
									/>
									<Box>
										<Typography
											component="label"
											sx={(theme) => ({
												fontWeight: 600,
												display: 'block',
												marginBottom: theme.spacing(1),
											})}
										>
											Answer
										</Typography>
										<AdminFieldContainer>
											<Stack direction="row" spacing={2} alignItems="center">
												<Typography>False</Typography>
												<Switch
													checked={boolAnswer}
													onChange={(e) => setBoolAnswer(e.target.checked)}
													inputProps={{ 'aria-label': 'True or false answer' }}
												/>
												<Typography>True</Typography>
											</Stack>
										</AdminFieldContainer>
									</Box>
								</Stack>
							)}

							{questionKind === 'single_choice_questions' && (
								<Stack spacing={2}>
									<AdminInputText
										required
										labelPlacement="above"
										label="Title"
										value={title}
										onChange={(e) => setTitle(e.target.value)}
									/>
									<AdminInputText
										labelPlacement="above"
										label="Hint"
										value={hint}
										onChange={(e) => setHint(e.target.value)}
									/>
									<Box>
										<Typography
											component="label"
											sx={(theme) => ({
												fontWeight: 600,
												display: 'block',
												marginBottom: theme.spacing(1),
												'& .req': { color: theme.palette.error.main },
											})}
										>
											Options<span className="req"> *</span>
										</Typography>
										<AdminFieldContainer required>
											<AdminStringList
												defaultMessage=""
												allowDuplicates={false}
												value={options}
												onChange={setOptions}
											/>
										</AdminFieldContainer>
									</Box>
									<AdminInputText
										required
										labelPlacement="above"
										label="Correct Answer"
										value={correctAnswer}
										onChange={(e) => setCorrectAnswer(e.target.value)}
									/>
								</Stack>
							)}

							{questionKind === 'multiple_choice_questions' && (
								<Stack spacing={2}>
									<AdminInputText
										required
										labelPlacement="above"
										label="Title"
										value={title}
										onChange={(e) => setTitle(e.target.value)}
									/>
									<AdminInputText
										labelPlacement="above"
										label="Hint"
										value={hint}
										onChange={(e) => setHint(e.target.value)}
									/>
									<Box>
										<Typography
											component="label"
											sx={(theme) => ({
												fontWeight: 600,
												display: 'block',
												marginBottom: theme.spacing(1),
												'& .req': { color: theme.palette.error.main },
											})}
										>
											All Options<span className="req"> *</span>
										</Typography>
										<AdminFieldContainer required>
											<AdminStringList
												defaultMessage=""
												allowDuplicates={false}
												value={options}
												onChange={setOptions}
											/>
										</AdminFieldContainer>
									</Box>
									<Box>
										<Typography
											component="label"
											sx={(theme) => ({
												fontWeight: 600,
												display: 'block',
												marginBottom: theme.spacing(1),
												'& .req': { color: theme.palette.error.main },
											})}
										>
											Corrrect Answers<span className="req"> *</span>
										</Typography>
										<AdminFieldContainer required>
											<AdminStringList
												defaultMessage=""
												allowDuplicates={false}
												value={correctAnswers}
												onChange={setCorrectAnswers}
											/>
										</AdminFieldContainer>
									</Box>
								</Stack>
							)}
						</Stack>
					</Box>
				</Box>

				<Box className={classes.sidebarColumn} component="aside" aria-label="Question metadata">
					<Box className={classes.saveAboveInfo}>
						<Button
							variant="outlined"
							className={classes.saveBtn}
							onClick={() => void handleSave()}
							disabled={pending}
						>
							Save
						</Button>
					</Box>
					<Box className={classes.infoTile}>
						<Typography component="h2" className={classes.infoSectionTitle}>
							Information
						</Typography>
						<Box className={classes.infoMetaBlock}>
							<Box className={classes.infoKvRow}>
								<Typography component="span" className={classes.infoKvLabel}>
									Last update
								</Typography>
								<Typography component="span" className={classes.infoKvValue}>
									now
								</Typography>
							</Box>
							<Box className={classes.infoKvRow}>
								<Typography component="span" className={classes.infoKvLabel}>
									By
								</Typography>
								<Typography component="span" className={classes.infoKvValue}>
									—
								</Typography>
							</Box>
						</Box>
						<Box className={classes.infoMetaBlock}>
							<Box className={classes.infoKvRow}>
								<Typography component="span" className={classes.infoKvLabel}>
									Created
								</Typography>
								<Typography component="span" className={classes.infoKvValue}>
									now
								</Typography>
							</Box>
							<Box className={classes.infoKvRow}>
								<Typography component="span" className={classes.infoKvLabel}>
									By
								</Typography>
								<Typography component="span" className={classes.infoKvValue}>
									—
								</Typography>
							</Box>
						</Box>
					</Box>
				</Box>
			</Box>
		</Box>
	);
};

export default AddQuizNewQuestionForm;
